import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Message,
  Modal,
  TextArea,
} from 'semantic-ui-react';
import styled from 'styled-components';
import {
  CritiqueCheckpoint,
  CritiqueGroup,
  DeliverableType,
  useAddCheckpoint,
  useUpdateCheckpoint,
} from 'src/hooks/useCritiqueGroups';
import { useCritiqueBooksParts } from 'src/hooks/useCritiqueBookParts';
import { bookRefId, bookRefTitle } from 'src/utils/critiqueGroupUtil';

/**
 * Design D2 — create / edit a checkpoint.
 *
 * Deliverable rules mirror the server's validateCheckpoint so a host is told
 * what's wrong before a round-trip; the server still validates everything.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const Choice = styled.div<{ $on: boolean }>`
  border: ${({ $on }) => ($on ? '2px solid #00b5ad' : '1.5px solid #e7e3d9')};
  background: ${({ $on }) => ($on ? '#f2fbfa' : '#fff')};
  border-radius: 9px;
  padding: 10px;
  font-size: 12.5px;
  font-weight: ${({ $on }) => ($on ? 700 : 400)};
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
`;

const Toggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--br-raised-surface);
  border: 1px solid #eee7da;
  border-radius: 10px;
  padding: 12px 14px;
  gap: 12px;
`;

const Switch = styled.button<{ $on: boolean }>`
  width: 44px;
  height: 25px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  background: ${({ $on }) => ($on ? '#00b5ad' : '#d0cabb')};
  transition: background 0.15s ease;
  span {
    position: absolute;
    top: 3px;
    ${({ $on }) => ($on ? 'right: 3px;' : 'left: 3px;')}
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: var(--br-surface);
  }
`;

/** `<input type="date">` needs yyyy-mm-dd in LOCAL time, not a UTC slice. */
const toDateInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Interpret the picked day in LOCAL time and send end-of-day.
 *
 * `new Date('2026-08-06')` parses as UTC midnight, which is the previous day
 * west of Greenwich — a host in New York would set "Aug 6" and see "Aug 5".
 * End-of-day also matches the intent: a checkpoint is due by the close of that
 * day, so it isn't overdue at 00:01.
 */
const fromDateInput = (value: string): string => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59).toISOString();
};

type Props = {
  group: CritiqueGroup;
  /** Absent = create. */
  checkpoint?: CritiqueCheckpoint;
  open: boolean;
  onClose: () => void;
};

/** Types whose deliverable can target a part. Only reached_part REQUIRES one. */
const TYPES_WITH_PART: Array<DeliverableType | 'none'> = ['reached_part', 'comment'];

const CheckpointEditor: React.FC<Props> = ({ group, checkpoint, open, onClose }) => {
  const { t } = useTranslation();
  const add = useAddCheckpoint(group._id);
  const update = useUpdateCheckpoint(group._id);
  const editing = !!checkpoint;

  const [label, setLabel] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [type, setType] = useState<DeliverableType | 'none'>('none');
  const [description, setDescription] = useState('');
  const [required, setRequired] = useState(true);
  const [part, setPart] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Deduped: one query and one option per book even if a book is linked twice,
  // which would otherwise emit duplicate React keys and ambiguous options.
  const books = useMemo(() => {
    const seen = new Set<string>();
    return (group.books || [])
      .map(b => ({ _id: bookRefId(b), title: bookRefTitle(b) }))
      .filter(b => {
        if (!b._id || seen.has(b._id)) return false;
        seen.add(b._id);
        return true;
      });
  }, [group.books]);
  const wantsPart = TYPES_WITH_PART.includes(type);
  const bookIds = useMemo(() => books.map(b => b._id), [books]);
  const bookParts = useCritiqueBooksParts(bookIds, { enabled: open && wantsPart });

  useEffect(() => {
    if (!open) return;
    setLabel(checkpoint?.label || '');
    setDueAt(toDateInput(checkpoint?.dueAt));
    setType(checkpoint?.deliverable?.type || 'none');
    setDescription(checkpoint?.deliverable?.description || '');
    setRequired(checkpoint?.deliverable?.required !== false);
    // The server reads `deliverable.part || checkpoint.part` (its effectivePart),
    // so prefill the same way — otherwise editing a checkpoint that only carries
    // the fallback would look partless and re-save as one.
    setPart(checkpoint?.deliverable?.part || checkpoint?.part);
    setError(undefined);
  }, [open, checkpoint?._id]);

  const types: Array<{ key: DeliverableType | 'none'; icon: string; label: string }> = [
    { key: 'none', icon: 'flag checkered', label: t('CritiqueDelivNone', 'Just a milestone') },
    { key: 'reached_part', icon: 'book', label: t('CritiqueDelivReachPart', 'Read to here') },
    { key: 'comment', icon: 'comment', label: t('CritiqueDelivComment', 'Leave a comment') },
    { key: 'custom', icon: 'pencil', label: t('CritiqueDelivCustomShort', 'Custom (self-mark)') },
  ];

  // One flat option list across every linked book — the server takes a part from
  // any of them. Two books can both have a "Chapter 1", so the book name is
  // prefixed once there's more than one to tell them apart.
  const multiBook = books.length > 1;
  const partOptions = books.flatMap((book, bookIndex) =>
    (bookParts[bookIndex]?.parts || []).map((p, i) => {
      const chapter = p.title || t('CritiqueChapterN', 'Chapter {{n}}', { n: i + 1 });
      return {
        key: p._id,
        value: p._id,
        text: multiBook
          ? `${book.title || t('CritiqueUntitled', 'Untitled')} · ${chapter}`
          : chapter,
      };
    })
  );
  const partsLoading = bookParts.some(b => b.isLoading);
  const partsFailed = bookParts.some(b => b.isError);
  const hasOptions = partOptions.length > 0;

  // A part saved against an earlier content version isn't in the list (the hook
  // only reads the current one). Without a stand-in the dropdown would render
  // blank over a live value — the host couldn't see what the checkpoint targets
  // and would re-save it unknowingly.
  const staleOption =
    part && !partsLoading && !partsFailed && hasOptions && !partOptions.some(o => o.value === part)
      ? [
          {
            key: part,
            value: part,
            text: t('CritiqueCpPartStale', 'Chapter from an earlier version'),
          },
        ]
      : [];
  const options = [...partOptions, ...staleOption];

  // Only a TOTAL failure hides the picker. With several linked books, one
  // unreadable book must not block picking a chapter from the others — that
  // would just be the same dead end wearing a different hat.
  const allFailed = partsFailed && !hasOptions && !partsLoading;
  const noChapters = !partsLoading && !partsFailed && !hasOptions;
  /** reached_part can't be saved without a part — the server 400s on it. */
  const missingPart = type === 'reached_part' && !part;

  const submit = () => {
    setError(undefined);
    if (!label.trim()) {
      setError(t('CritiqueCpLabelRequired', 'Give the checkpoint a name.'));
      return;
    }
    if (!dueAt) {
      setError(t('CritiqueCpDateRequired', 'Pick a date.'));
      return;
    }
    if (type === 'custom' && !description.trim()) {
      setError(
        t('CritiqueCpCustomDescRequired', 'Describe what members should do for a custom deliverable.')
      );
      return;
    }
    // Belt-and-braces: the Save button is already disabled while this holds, so
    // this only catches a caller that bypasses it.
    if (missingPart) {
      setError(t('CritiqueCpPartRequired', 'Pick the chapter members should read to.'));
      return;
    }

    const deliverable =
      type === 'none'
        ? null
        : {
            type: type as DeliverableType,
            required,
            ...(type === 'custom' ? { description: description.trim() } : {}),
            // Omitted when unset: the deliverable replaces the stored one, so
            // leaving the key out is how a comment's part gets cleared.
            ...(wantsPart && part ? { part } : {}),
          };

    const payload = {
      label: label.trim(),
      dueAt: fromDateInput(dueAt),
      deliverable,
    };

    const onError = (err: any) => setError(err?.message);
    if (editing && checkpoint) {
      update.mutate(
        { checkpointId: checkpoint._id, ...payload },
        { onSuccess: onClose, onError }
      );
    } else {
      add.mutate(payload as any, { onSuccess: onClose, onError });
    }
  };

  const busy = add.isLoading || update.isLoading;

  return (
    <Modal open={open} onClose={onClose} size="tiny" data-testid="checkpoint-editor">
      <Modal.Header>
        {editing
          ? t('CritiqueEditCheckpoint', 'Edit checkpoint')
          : t('CritiqueNewCheckpoint', 'New checkpoint')}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label>{t('CritiqueCpLabel', 'Label')}</label>
            <Input
              value={label}
              onChange={(_e, d) => setLabel(d.value)}
              placeholder={t('CritiqueCpLabelPlaceholder', 'e.g. Klar med kapitel 1')}
              data-testid="checkpoint-label-input"
            />
          </Form.Field>

          <Form.Field>
            <label>{t('CritiqueCpDate', 'Date')}</label>
            <Input
              type="date"
              value={dueAt}
              onChange={(_e, d) => setDueAt(d.value)}
              data-testid="checkpoint-date-input"
            />
          </Form.Field>

          <Form.Field>
            <label>{t('CritiqueCpDeliverable', 'Deliverable — what members must do')}</label>
            <Grid>
              {types.map(x => (
                <Choice
                  key={x.key}
                  $on={type === x.key}
                  onClick={() => setType(x.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') setType(x.key);
                  }}
                  data-testid={`checkpoint-type-${x.key}`}
                >
                  <Icon name={x.icon as any} style={{ color: type === x.key ? '#00b5ad' : '#8a857b' }} />
                  {x.label}
                </Choice>
              ))}
            </Grid>
            {/* 'survey' is a valid server type but needs a survey picker (and a
                survey linked to the book); left out until that exists rather
                than shipping a control that can only 400. */}
          </Form.Field>

          {wantsPart && (
            <Form.Field data-testid="checkpoint-part-field">
              <label>
                {type === 'reached_part'
                  ? t('CritiqueCpPartRequiredLabel', 'Read to which chapter?')
                  : t('CritiqueCpPartOptionalLabel', 'Chapter (optional)')}
              </label>
              {books.length === 0 ? (
                <Message info size="small" data-testid="checkpoint-part-no-books">
                  {t(
                    'CritiqueCpPartNoBooks',
                    'Link a book to this group first — a chapter has to come from the group’s book.'
                  )}
                </Message>
              ) : allFailed ? (
                <Message negative size="small" data-testid="checkpoint-part-load-failed">
                  {t('CritiqueCpPartLoadFailed', 'Could not load the chapters. Try again.')}
                </Message>
              ) : noChapters ? (
                <Message info size="small" data-testid="checkpoint-part-no-chapters">
                  {t(
                    'CritiqueCpPartNoChapters',
                    'The linked book has no chapters yet. Upload a manuscript to pick one.'
                  )}
                </Message>
              ) : (
                <>
                  <Dropdown
                    selection
                    search
                    clearable={type === 'comment'}
                    loading={partsLoading}
                    disabled={partsLoading}
                    options={options}
                    value={part || ''}
                    onChange={(_e, d) => setPart((d.value as string) || undefined)}
                    placeholder={
                      partsLoading
                        ? t('CritiqueCpPartLoading', 'Loading chapters…')
                        : t('CritiqueCpPartPlaceholder', 'Pick a chapter')
                    }
                    data-testid="checkpoint-part-select"
                  />
                  {partsFailed && (
                    <Message
                      warning
                      size="small"
                      visible
                      data-testid="checkpoint-part-partial-failure"
                      style={{ marginTop: 8 }}
                    >
                      {t(
                        'CritiqueCpPartSomeBooksFailed',
                        'Some chapters could not be loaded, so this list may be incomplete.'
                      )}
                    </Message>
                  )}
                </>
              )}
              <div style={{ fontSize: 11.5, color: '#8a857b', marginTop: 6 }}>
                {type === 'reached_part'
                  ? t(
                      'CritiqueCpPartRequiredHelp',
                      'Required — members are done once they’ve read this far.'
                    )
                  : t(
                      'CritiqueCpPartOptionalHelp',
                      'Optional — pin the comment to a chapter, or leave it open.'
                    )}
              </div>
            </Form.Field>
          )}

          {type === 'custom' && (
            <Form.Field>
              <label>{t('CritiqueCpCustomWhat', 'What should they do?')}</label>
              <TextArea
                rows={2}
                value={description}
                onChange={(_e, d) => setDescription(String(d.value ?? ''))}
                placeholder={t('CritiqueCpCustomPlaceholder', 'e.g. post your 1-page critique')}
                data-testid="checkpoint-description-input"
              />
            </Form.Field>
          )}

          {type !== 'none' && (
            <Toggle>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueCpRequired', 'Required')}
                </div>
                <div style={{ fontSize: 11.5, color: '#8a857b' }}>
                  {t(
                    'CritiqueCpRequiredHelp',
                    'Drives reminders & blocks “done”. Off = guiding only.'
                  )}
                </div>
              </div>
              <Switch
                type="button"
                $on={required}
                onClick={() => setRequired(r => !r)}
                aria-pressed={required}
                aria-label={t('CritiqueCpRequired', 'Required')}
                data-testid="checkpoint-required-toggle"
              >
                <span />
              </Switch>
            </Toggle>
          )}

          {error && (
            <Message negative size="small" data-testid="checkpoint-error">
              {error}
            </Message>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} style={{ borderRadius: 9 }}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          color="teal"
          onClick={submit}
          loading={busy}
          // Disabled rather than letting the save 400 with "A reached_part
          // deliverable needs a part" — the field above says what's missing.
          disabled={busy || missingPart}
          style={{ borderRadius: 9, fontWeight: 700 }}
          data-testid="checkpoint-save"
        >
          {t('CritiqueSaveCheckpoint', 'Save checkpoint')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CheckpointEditor;
